/* eslint-disable react-hooks/exhaustive-deps */
import { PropsWithChildren, useEffect, useState } from "react";
import { useAuthActions } from "@core/hooks/useAuthActions";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { useNavigate } from "@remix-run/react";

interface AuthGuardProps {
  requiresPermission?: string;
  requiresAnyPermission?: string[];
  requiresRole?: string;
  requiresAnyRole?: string[];
  requiresAuth?: boolean;
  requiresNoAuth?: boolean;
  requireNotRole?: string;
  fallback?: JSX.Element | (() => JSX.Element);
  redirectPath?: string;
  delay?: number;
}

/* AuthGuard will never be used at the page level. You can handle that in the loader. */
export const AuthGuard = ({
  children,
  delay,
  fallback,
  redirectPath,
  requiresAuth,
  requiresNoAuth,
  requiresPermission,
  requiresAnyPermission,
  requiresRole,
  requiresAnyRole,
  requireNotRole
}: PropsWithChildren<AuthGuardProps>): JSX.Element | null => {
  const [ canView, setCanView ] = useState<boolean>(false);
  const [ isLoading, setIsLoading ] = useState<boolean>();

  const user = useCurrentUser();
  const navigate = useNavigate();
  const { hasPermission, hasRole } = useAuthActions();

  const handleRedirect = () => {
    if (!redirectPath) {
      return;
    }
    const t = setTimeout(() => {
      clearTimeout(t);
      const newPath = redirectPath;
      navigate(newPath, { replace: true });
    }, delay ?? 3000);
  };

  const checkPermissions = async (): Promise<boolean> => {
    if (requiresPermission) {
      return await hasPermission(requiresPermission);
    }
    if (requiresAnyPermission) {
      const results = await Promise.all(requiresAnyPermission.map(hasPermission));
      return results.some(Boolean);
    }
    return true;
  };

  const checkRoles = async (): Promise<boolean> => {
    if (requireNotRole) {
      return !await hasRole(requireNotRole);
    }
    if (requiresRole) {
      return await hasRole(requiresRole);
    }
    if (requiresAnyRole) {
      const results = await Promise.all(requiresAnyRole.map(hasRole));
      return results.some(Boolean);
    }
    return true;
  };

  const checkAuth = (): boolean => {
    if (requiresAuth) {
      return (user?.user?.Id ?? 0) > 0;
    }
    if (requiresNoAuth) {
      return !user?.user?.Id;
    }
    return true;
  };

  const determineIfViewable = async (): Promise<boolean> => {
    if (requiresRole || requiresAnyRole || requireNotRole) {
      return await checkRoles();
    }
    if (requiresPermission || requiresAnyPermission) {
      return await checkPermissions();
    }
    if (requiresAuth || requiresNoAuth) {
      return checkAuth();
    }
    return true;
  };

  const load = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const canViewRes = await determineIfViewable();
      setCanView(canViewRes);
      setIsLoading(false);
      if (!canViewRes) {
        handleRedirect();
      }
    } catch (err) {
      setCanView(false);
      setIsLoading(false);
      handleRedirect();
    }
  };

  useEffect(() => {
    if (user.isLoading) {
      return;
    }
    load();
  }, [ user.isLoading, user?.user?.Id ]);

  if (isLoading) {
    return null;
  }

  if (!canView) {
    if (fallback) {
      if (typeof fallback == "function") {
        const Component = fallback;
        return <Component />;
      }
      return fallback;
    }
    return null;
  }

  return <>{children}</>;
};
